/**
 * Project Detail → Attendances tab. 1:1 with the design folder's
 * `project-details.html#ptab-attendances` and the mobile app's
 * `src/features/projects/tabs/AttendanceTab.tsx`.
 *
 * Layout:
 *   2-col grid (1fr left | 340px right rail)
 *     LEFT
 *       - Date pager (prev / picker / next + "Today" jump)
 *       - Day Summary card — N workers · M hrs · X payable units +
 *         3 pills (present/half/absent) + "Mark all unmarked as
 *         present" bulk action
 *       - Labour roster — per-worker card with avatar + name/role/
 *         pay + status pill + P/H/A toggle
 *       - "+ Add Worker" CTA at the bottom
 *     RIGHT rail
 *       - This Month Summary — total payout (est), working days,
 *         payable units, avg workforce
 *
 * Data:
 *   - `useEffectiveProjectLabour` for the roster (merges
 *     projectLabour + any historical labour derived from
 *     attendance, with silent backfill).
 *   - `useProjectAttendance` for the picked day's attendance docs.
 *   - `useProjectAttendanceAll` for the right-rail monthly summary.
 *
 * Writes:
 *   - `markAttendance` on every P/H/A click — idempotent (same doc
 *     id, overwrites status).
 *   - `updateAttendanceStatus` is the same write; for clarity we
 *     just call `markAttendance` everywhere so we don't need a
 *     conditional code path for "already exists" vs "new".
 *   - `LabourFormSlideOver` handles roster create / edit / remove.
 *
 * Mobile parity 100%: same Firestore paths, same field names,
 * same doc-id conventions. A status set on mobile flips on web
 * within ~1s and vice versa.
 */
import { useMemo, useState } from 'react';

import { canMarkAttendance } from '@shared/perms';

import { useAuth } from '@/auth/AuthProvider';
import { LabourDetailSlideOver } from '@/components/LabourDetailSlideOver';
import { LabourFormSlideOver } from '@/components/LabourFormSlideOver';
import {
  STATUS_LABEL,
  avatarTintFor,
  formatLongDate,
  hoursFor,
  initialOf,
  markAttendance,
  payableUnitFor,
  shiftDateKey,
  todayLocalDateKey,
  type AttendanceRecord,
  type AttendanceStatus,
  type AttendanceUiStatus,
  type ProjectLabour,
} from '@/lib/attendance';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import { useEffectiveProjectLabour } from '@/lib/useEffectiveProjectLabour';
import {
  useProjectAttendance,
  useProjectAttendanceAll,
} from '@/lib/useProjectAttendance';
import type { ProjectListItem } from '@/lib/useProjects';

// ───── Component ─────

export function AttendanceTab({ project }: { project: ProjectListItem }) {
  const { user } = useAuth();
  const orgId = project.orgId ?? null;
  const { role: orgRole } = useCurrentOrgRole();
  const canWrite = canMarkAttendance(orgRole);

  const [dateKey, setDateKey] = useState<string>(() => todayLocalDateKey());
  const { data: roster, loading: rosterLoading } = useEffectiveProjectLabour(
    orgId,
    project.id,
  );
  const { data: dayRecords, loading: dayLoading } = useProjectAttendance(
    orgId,
    project.id,
    dateKey,
  );
  const { data: allRecords } = useProjectAttendanceAll(orgId, project.id);

  // Per-row pending state (so P/H/A taps feel instant while the
  // Firestore write lands).
  const [pending, setPending] = useState<Set<string>>(new Set());

  // Form slide-over state (create new worker / edit existing).
  const [formMode, setFormMode] = useState<
    | { kind: 'closed' }
    | { kind: 'create' }
    | { kind: 'edit'; labour: ProjectLabour }
  >({ kind: 'closed' });

  // Worker detail slide-over (full month-by-month history).
  const [detailLabour, setDetailLabour] = useState<ProjectLabour | null>(null);

  const isToday = dateKey === todayLocalDateKey();

  // Quick lookup: labourId → its record for this day (if marked).
  const byLabourId = useMemo(() => {
    const m = new Map<string, AttendanceRecord>();
    for (const r of dayRecords) m.set(r.labourId, r);
    return m;
  }, [dayRecords]);

  // Day-summary KPIs.
  const dayStats = useMemo(() => {
    let present = 0;
    let half = 0;
    let absent = 0;
    let payableUnits = 0;
    let hours = 0;
    let workers = 0;
    for (const lab of roster) {
      const rec = byLabourId.get(lab.labourId);
      const status: AttendanceUiStatus = rec ? rec.status : 'unmarked';
      if (status === 'present') {
        present++;
        workers++;
      } else if (status === 'half_day') {
        half++;
        workers++;
      } else if (status === 'absent') {
        absent++;
      }
      payableUnits += payableUnitFor(status);
      hours += hoursFor(status);
    }
    const unmarked = roster.length - present - half - absent;
    return { present, half, absent, unmarked, payableUnits, hours, workers };
  }, [roster, byLabourId]);

  // Monthly summary — derived from full attendance subscription.
  const monthlySummary = useMemo(() => {
    const [yStr, mStr] = dateKey.split('-');
    const monthPrefix = `${yStr}-${mStr}`; // matches "YYYY-MM" of every doc.date
    let totalPayout = 0;
    let payableUnits = 0;
    const workingDays = new Set<string>();
    const dailyWorkers = new Map<string, Set<string>>();
    for (const r of allRecords) {
      if (!r.date?.startsWith(monthPrefix)) continue;
      const unit = payableUnitFor(r.status as AttendanceUiStatus);
      if (unit > 0) {
        workingDays.add(r.date);
        payableUnits += unit;
        const dailySet = dailyWorkers.get(r.date) ?? new Set<string>();
        dailySet.add(r.labourId);
        dailyWorkers.set(r.date, dailySet);
        const rate = typeof r.payRate === 'number' ? r.payRate : 0;
        // Hour-paid → 8h equivalent for a full day, 4h for half.
        if (r.payUnit === 'hour') {
          totalPayout += rate * hoursFor(r.status as AttendanceUiStatus);
        } else {
          totalPayout += rate * unit;
        }
      }
    }
    let avgWorkforce = 0;
    if (workingDays.size > 0) {
      let sum = 0;
      for (const s of dailyWorkers.values()) sum += s.size;
      avgWorkforce = Math.round(sum / workingDays.size);
    }
    const monthLabel = new Date(
      parseInt(yStr, 10),
      parseInt(mStr, 10) - 1,
      1,
    ).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    return {
      monthLabel,
      totalPayout,
      payableUnits,
      workingDayCount: workingDays.size,
      avgWorkforce,
    };
  }, [allRecords, dateKey]);

  // ── Actions ────────────────────────────────────────────────

  const onMark = async (lab: ProjectLabour, status: AttendanceStatus) => {
    if (!orgId || !user) return;
    const key = `${lab.labourId}:${dateKey}`;
    setPending((prev) => new Set(prev).add(key));
    try {
      await markAttendance({
        orgId,
        projectId: project.id,
        labourId: lab.labourId,
        labourName: lab.labourName,
        labourRole: lab.labourRole,
        description: lab.description,
        payRate: lab.payRate,
        payUnit: lab.payUnit,
        date: dateKey,
        status,
        createdBy: user.uid,
      });
    } catch (e) {
      console.warn('[AttendanceTab] mark failed:', e);
    } finally {
      setPending((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const onMarkAllPresent = async () => {
    if (!orgId || !user) return;
    const targets = roster.filter((lab) => !byLabourId.has(lab.labourId));
    if (targets.length === 0) return;
    await Promise.all(
      targets.map((lab) =>
        markAttendance({
          orgId,
          projectId: project.id,
          labourId: lab.labourId,
          labourName: lab.labourName,
          labourRole: lab.labourRole,
          description: lab.description,
          payRate: lab.payRate,
          payUnit: lab.payUnit,
          date: dateKey,
          status: 'present',
          createdBy: user.uid,
        }),
      ),
    );
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 340px',
        gap: 24,
        alignItems: 'flex-start',
      }}
    >
      {/* ─── LEFT: pager + summary + roster ─── */}
      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Date pager */}
        <div className="att-date-pager card">
          <button
            type="button"
            className="btn-icon"
            onClick={() => setDateKey((k) => shiftDateKey(k, -1))}
            aria-label="Previous day"
            title="Previous day"
            style={{
              padding: 8,
              border: '1px solid var(--border)',
              borderRadius: 6,
              cursor: 'pointer',
              background: '#fff',
            }}
          >
            <i className="ph-bold ph-caret-left" />
          </button>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: 'var(--text-secondary)',
                letterSpacing: '0.5px',
              }}
            >
              SELECTED DATE
            </div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: 'var(--text-primary)',
                marginTop: 2,
              }}
            >
              {formatLongDate(dateKey)}
              {isToday ? (
                <span
                  style={{
                    marginLeft: 8,
                    fontSize: 10,
                    fontWeight: 700,
                    padding: '2px 6px',
                    borderRadius: 4,
                    background: 'var(--brand-bg)',
                    color: 'var(--brand)',
                    textTransform: 'uppercase',
                  }}
                >
                  Today
                </span>
              ) : null}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="date"
              value={dateKey}
              onChange={(e) =>
                setDateKey(e.currentTarget.value || todayLocalDateKey())
              }
              max={todayLocalDateKey()}
              style={{
                padding: '8px 10px',
                border: '1px solid var(--border)',
                borderRadius: 6,
                fontSize: 12,
                background: '#fff',
                color: 'var(--text-primary)',
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
              title="Pick a date"
            />
            <button
              type="button"
              className="btn-icon"
              onClick={() => setDateKey((k) => shiftDateKey(k, 1))}
              aria-label="Next day"
              title="Next day"
              disabled={isToday}
              style={{
                padding: 8,
                border: '1px solid var(--border)',
                borderRadius: 6,
                cursor: isToday ? 'not-allowed' : 'pointer',
                background: '#fff',
                opacity: isToday ? 0.3 : 1,
              }}
            >
              <i className="ph-bold ph-caret-right" />
            </button>
          </div>
        </div>

        {/* Day summary */}
        <div
          className="card att-summary-card"
          style={{ marginBottom: 16 }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 16px',
              background: '#F9FAFB',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: 'var(--text-secondary)',
                letterSpacing: '0.5px',
              }}
            >
              DAY TOTAL · PAYABLE
            </div>
            {canWrite ? (
              // Hidden for roles that can't write — Firestore rule blocks it anyway
              <button
                type="button"
                className="btn-secondary"
                onClick={onMarkAllPresent}
                disabled={
                  dayStats.unmarked === 0 || rosterLoading || roster.length === 0
                }
                style={{ fontSize: 11, padding: '4px 10px', fontWeight: 600 }}
              >
                MARK ALL AS PRESENT
              </button>
            ) : null}
          </div>
          <div
            style={{
              padding: 16,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 16,
            }}
          >
            <div>
              <div className="att-summary-headline">
                {dayStats.workers}{' '}
                {dayStats.workers === 1 ? 'worker' : 'workers'} ·{' '}
                {dayStats.hours} hrs
              </div>
              <div className="att-summary-units">
                {dayStats.payableUnits.toFixed(1)} PAYABLE UNITS
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                alignItems: 'flex-end',
              }}
            >
              <div className="att-pill present">{dayStats.present} Present</div>
              <div className="att-pill half">{dayStats.half} Half</div>
              <div className="att-pill absent">{dayStats.absent} Absent</div>
            </div>
          </div>
        </div>

        {/* Labour list */}
        {rosterLoading || dayLoading ? (
          <div
            className="card"
            style={{
              padding: 32,
              textAlign: 'center',
              color: 'var(--text-secondary)',
            }}
          >
            Loading roster…
          </div>
        ) : roster.length === 0 ? (
          <div className="card" style={{ padding: 32, textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}>
              <i className="ph ph-hard-hat" />
            </div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              No workers on this project yet
            </div>
            <div
              style={{
                fontSize: 13,
                color: 'var(--text-secondary)',
                maxWidth: 360,
                margin: '0 auto 16px',
              }}
            >
              Add a worker to start tracking daily attendance. Their roster
              entry will appear here every day until you remove them.
            </div>
            {canWrite ? (
              // Hidden for roles that can't write — Firestore rule blocks it anyway
              <button
                type="button"
                className="btn-primary"
                onClick={() => setFormMode({ kind: 'create' })}
              >
                <i className="ph-bold ph-plus" /> Add Worker
              </button>
            ) : null}
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              marginBottom: 16,
            }}
          >
            {roster.map((lab) => {
              const rec = byLabourId.get(lab.labourId);
              const status: AttendanceUiStatus = rec
                ? (rec.status as AttendanceUiStatus)
                : 'unmarked';
              const key = `${lab.labourId}:${dateKey}`;
              const isPending = pending.has(key);
              return (
                <AttendanceRow
                  key={lab.labourId}
                  lab={lab}
                  status={isPending ? 'loading' : status}
                  onMark={(s) => onMark(lab, s)}
                  onEdit={() => setDetailLabour(lab)}
                  canWrite={canWrite}
                />
              );
            })}

            {/* Footer add button */}
            {canWrite ? (
              // Hidden for roles that can't write — Firestore rule blocks it anyway
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setFormMode({ kind: 'create' })}
                style={{
                  marginTop: 8,
                  justifyContent: 'center',
                  borderStyle: 'dashed',
                }}
              >
                <i className="ph-bold ph-plus" /> Add Worker
              </button>
            ) : null}
          </div>
        )}
      </div>

      {/* ─── RIGHT rail ─── */}
      <div className="att-right-panel">
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div
            className="card-header"
            style={{
              background: '#F9FAFB',
              borderBottom: '1px solid var(--border)',
              padding: '12px 16px',
            }}
          >
            <div
              className="card-title"
              style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}
            >
              {monthlySummary.monthLabel} Summary
            </div>
          </div>
          <div style={{ padding: 16 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: 'rgba(16,185,129,0.1)',
                  color: '#10B981',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i className="ph-bold ph-coins" style={{ fontSize: 20 }} />
              </div>
              <div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--text-secondary)',
                  }}
                >
                  Total Payout (est)
                </div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: 'var(--text-primary)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  ₹{Math.round(monthlySummary.totalPayout).toLocaleString('en-IN')}
                </div>
              </div>
            </div>

            <div
              style={{
                borderTop: '1px solid var(--border)',
                paddingTop: 12,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <SummaryRow
                label="Total Working Days"
                value={`${monthlySummary.workingDayCount} ${monthlySummary.workingDayCount === 1 ? 'day' : 'days'}`}
              />
              <SummaryRow
                label="Total Payable Units"
                value={`${monthlySummary.payableUnits.toFixed(1)} units`}
              />
              <SummaryRow
                label="Avg. Daily Workforce"
                value={`${monthlySummary.avgWorkforce} ${monthlySummary.avgWorkforce === 1 ? 'worker' : 'workers'}`}
              />
            </div>
          </div>
        </div>

        <div
          className="card"
          style={{
            padding: 16,
            fontSize: 12,
            color: 'var(--text-tertiary)',
            lineHeight: 1.5,
          }}
        >
          <div
            style={{
              fontWeight: 700,
              fontSize: 11,
              color: 'var(--text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: 6,
            }}
          >
            <i className="ph ph-info" /> Tips
          </div>
          Click <b>P / H / A</b> on any worker row to mark them present, half,
          or absent for the selected date. Attendance writes are live — mobile
          + web sync within ~1 second.
        </div>
      </div>

      {/* Labour ADD slide-over (create new worker). The detail
          slide-over has its own internal Edit button that mounts the
          form for editing existing workers, so the form here is
          create-only. */}
      <LabourFormSlideOver
        open={formMode.kind === 'create'}
        onClose={() => setFormMode({ kind: 'closed' })}
        orgId={orgId}
        projectId={project.id}
        labour={null}
      />

      {/* Per-worker DETAIL slide-over — opens on row click. Shows
          hero + lifetime totals + monthly breakdown with day-level
          status pills. Has its own "Edit" button that mounts the
          form on top for in-place edits. */}
      <LabourDetailSlideOver
        open={Boolean(detailLabour)}
        onClose={() => setDetailLabour(null)}
        orgId={orgId}
        projectId={project.id}
        labour={detailLabour}
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Row + helpers
// ────────────────────────────────────────────────────────────

function AttendanceRow({
  lab,
  status,
  onMark,
  onEdit,
  canWrite,
}: {
  lab: ProjectLabour;
  status: AttendanceUiStatus;
  onMark: (s: AttendanceStatus) => void;
  onEdit: () => void;
  // Disabled (kept in layout) for roles that can't write — Firestore rule blocks it anyway
  canWrite: boolean;
}) {
  const tint = avatarTintFor(lab.labourName);
  const pillClass =
    status === 'present'
      ? 'present'
      : status === 'half_day'
        ? 'half'
        : status === 'absent'
          ? 'absent'
          : 'unmarked';
  const isLoading = status === 'loading';
  return (
    <div
      className="card att-row"
      role="button"
      tabIndex={0}
      onClick={onEdit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onEdit();
      }}
      style={{ cursor: 'pointer' }}
      title="Open monthly history"
    >
      <div
        className="att-avatar"
        style={{ background: tint.bg, color: tint.fg }}
      >
        {initialOf(lab.labourName)}
      </div>
      <div className="att-info">
        <div
          style={{
            fontWeight: 600,
            fontSize: 14,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {lab.labourName}
        </div>
        <div
          style={{
            fontSize: 12,
            color: 'var(--text-secondary)',
            marginTop: 2,
          }}
        >
          {lab.labourRole}
          {typeof lab.payRate === 'number' && lab.payRate > 0 ? (
            <>
              {' · '}₹{lab.payRate.toLocaleString('en-IN')}/
              {lab.payUnit === 'hour' ? 'hr' : 'day'}
            </>
          ) : null}
        </div>
      </div>
      <div
        className="att-actions"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`att-status-pill ${pillClass}`}>
          {isLoading ? '…' : STATUS_LABEL[status]}
        </div>
        <div className="pha-toggle">
          <button
            type="button"
            className={`pha-btn p${status === 'present' ? ' active' : ''}`}
            onClick={() => onMark('present')}
            disabled={isLoading || !canWrite}
            title="Mark present"
          >
            P
          </button>
          <button
            type="button"
            className={`pha-btn h${status === 'half_day' ? ' active' : ''}`}
            onClick={() => onMark('half_day')}
            disabled={isLoading || !canWrite}
            title="Mark half day"
          >
            H
          </button>
          <button
            type="button"
            className={`pha-btn a${status === 'absent' ? ' active' : ''}`}
            onClick={() => onMark('absent')}
            disabled={isLoading || !canWrite}
            title="Mark absent"
          >
            A
          </button>
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
        {label}
      </span>
      <span style={{ fontSize: 13, fontWeight: 600 }}>{value}</span>
    </div>
  );
}
